import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Activity, AlertTriangle, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";

type Earthquake = {
  id: string;
  magnitude: number;
  place: string;
  time: string;
  url: string;
  tsunami: boolean;
  alert: string | null;
  felt: number | null;
  significance: number;
  lat: number;
  lng: number;
  depth: number;
};

const alertColors: Record<string, string> = {
  red: "bg-red-500/20 text-red-400 border-red-500/30",
  orange: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  yellow: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  green: "bg-green-500/20 text-green-400 border-green-500/30",
};

const magColor = (m: number) => {
  if (m >= 7) return "text-red-400";
  if (m >= 5) return "text-orange-400";
  if (m >= 4) return "text-yellow-400";
  return "text-green-400";
};

const EarthquakeMonitor = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["earthquakes"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("fetch-earthquakes");
      if (error) throw error;
      return data as { earthquakes: Earthquake[]; fetchedAt: string };
    },
    staleTime: 300000,
    refetchInterval: 300000,
    retry: 2,
  });

  const quakes = data?.earthquakes || [];
  const significant = quakes.filter((q) => q.magnitude >= 5);

  return (
    <section id="earthquakes" className="py-8">
      <div className="container mx-auto px-4">
        <div className="mb-6 flex items-center gap-3">
          <Activity className="h-5 w-5 text-orange-400" />
          <h2 className="font-mono text-lg font-bold uppercase tracking-wider text-foreground">
            Earthquake Monitor
          </h2>
          <Badge variant="outline" className="text-[9px] font-mono text-muted-foreground">
            USGS — Last 7 Days — M2.5+
          </Badge>
          {significant.length > 0 && (
            <Badge variant="outline" className="text-[9px] font-mono text-orange-400 border-orange-400/30 bg-orange-500/10">
              {significant.length} significant (M5+)
            </Badge>
          )}
        </div>

        {isLoading && !data ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            Loading seismic data...
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="grid grid-cols-[60px_1fr_80px_80px] gap-2 px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground border-b border-border">
              <span>Mag</span>
              <span>Location</span>
              <span className="text-right">Depth</span>
              <span className="text-right">Time</span>
            </div>
            <div className="divide-y divide-border/50 max-h-[400px] overflow-y-auto">
              {quakes.slice(0, 30).map((q) => (
                <a
                  key={q.id}
                  href={q.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="grid grid-cols-[60px_1fr_80px_80px] items-center gap-2 px-3 py-2.5 transition-colors hover:bg-accent/50"
                >
                  <div className="flex items-center gap-1.5">
                    <span className={`text-sm font-mono font-bold ${magColor(q.magnitude)}`}>
                      {q.magnitude.toFixed(1)}
                    </span>
                    {q.tsunami && <AlertTriangle className="h-3 w-3 text-red-400" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground truncate">{q.place}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      {q.alert && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded border ${alertColors[q.alert] || ""}`}>
                          {q.alert.toUpperCase()}
                        </span>
                      )}
                      {q.felt && (
                        <span className="text-[9px] text-muted-foreground">
                          Felt by {q.felt}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-right text-[11px] font-mono text-muted-foreground">
                    {q.depth.toFixed(1)} km
                  </p>
                  <p className="text-right text-[11px] font-mono text-muted-foreground">
                    {timeAgo(q.time)}
                  </p>
                </a>
              ))}
            </div>
          </div>
        )}

        {data?.fetchedAt && (
          <p className="mt-2 text-[10px] font-mono text-muted-foreground text-right">
            Source: USGS · Updated: {new Date(data.fetchedAt).toLocaleTimeString()}
          </p>
        )}
      </div>
    </section>
  );
};

function timeAgo(iso: string) {
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return `${Math.floor(ms / 60000)}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default EarthquakeMonitor;
